'use client';
import React, { useState, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import emailjs from '@emailjs/browser';

// ─── Types ───────────────────────────────────────────────────────────────────
interface BookingForm {
  name: string;
  phone: string;
  email: string;
  service: string;
  date: string;
  time: string;
  notes: string;
}

// ─── Data ────────────────────────────────────────────────────────────────────
const serviceOptions = [
  { category: '✂️ Hair Care', items: ['Hair Smoothening', 'Kerasmooth with Treatment', 'Hair Spa', 'Trendy Haircut', 'Hair Keratin', 'Hair Extensions'] },
  { category: '💄 Makeup & Bridal', items: ['Bridal Makeup', 'Party Makeup', 'Occasion Makeover', 'Photoshoot Styling', 'Personal Styling'] },
  { category: '💅 Nails & Beauty', items: ['Nail Art & Gel Polish', 'Eyelash Extensions', 'Microblading (Brows)', 'Lip Coloring', 'Tattoo Art', 'Piercing'] },
  { category: '💆 Skin & Body', items: ['Wine Facial', 'Aroma Therapy', 'Body Polishing', 'Customized Facial', 'Hydra Facial', 'Pedicure', 'Manicure'] },
];

const timeSlots = [
  '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '12:00 PM', '12:30 PM', '01:00 PM', '01:30 PM',
  '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM',
  '04:00 PM', '04:30 PM', '05:00 PM', '05:30 PM',
  '06:00 PM', '06:30 PM', '07:00 PM', '07:30 PM',
];

const EMAILJS_SERVICE_ID = process.env.NEXT_PUBLIC_EMAILJS_SERVICE_ID || '';
const EMAILJS_TEMPLATE_ID = process.env.NEXT_PUBLIC_EMAILJS_TEMPLATE_ID || '';
const EMAILJS_PUBLIC_KEY = process.env.NEXT_PUBLIC_EMAILJS_PUBLIC_KEY || '';

// ─── Main Component ───────────────────────────────────────────────────────────
export default function BookingPage() {
  const formRef = useRef<HTMLFormElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState<BookingForm>({
    name: '',
    phone: '',
    email: '',
    service: '',
    date: '',
    time: '',
    notes: '',
  });

  const today = new Date().toISOString().split('T')[0];

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleServiceSelect = (service: string) => {
    setForm({ ...form, service });
  };

  const handleTimeSelect = (time: string) => {
    setForm({ ...form, time });
  };

  const canProceedStep1 = form.service !== '';
  const canProceedStep2 = form.date !== '' && form.time !== '';
  const canSubmit = form.name.trim() !== '' && form.phone.trim() !== '' && form.email.trim() !== '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');

    const templateParams = {
      from_name: form.name,
      from_phone: form.phone,
      from_email: form.email,
      service: form.service,
      date: form.date,
      time: form.time,
      notes: form.notes || 'None',
      to_email: 'foreversrijita@gmail.com',
    };

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please call us directly at +91 98765 43210 to book.');
    } finally {
      setSubmitting(false);
    }
  };

  const navLinks = [
    { label: 'HOME', href: '/' },
    { label: 'ABOUT US', href: '/#why-us' },
    { label: 'SERVICES', href: '/#services' },
    { label: 'CONTACT US', href: '/#visit-us' },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#000', color: '#fff', fontFamily: 'Segoe UI, sans-serif' }}>

      {/* ── NAVBAR ── */}
      <nav className="sticky top-0 z-40 w-full" style={{ backgroundColor: '#000', borderBottom: '1px solid rgba(212,175,55,0.3)' }}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-3">
            <Image
              src="/assets/images/Hashtag_Logo-1788765015476.jpg"
              alt="Hashtag Hair & Beauty Studio Logo"
              width={50}
              height={50}
              className="rounded-full object-cover"
              style={{ border: '2px solid #d4af37' }}
            />
            <span className="hidden sm:block text-sm font-semibold tracking-widest" style={{ color: '#d4af37', fontFamily: 'Playfair Display, serif' }}>HASHTAG STUDIO</span>
          </Link>

          <ul className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <li key={link.label}>
                <Link href={link.href} className="nav-link-gold text-sm tracking-widest">{link.label}</Link>
              </li>
            ))}
            <li>
              <Link href="/booking" className="text-sm tracking-widest px-4 py-2 rounded-lg font-semibold gold-btn">BOOK NOW</Link>
            </li>
          </ul>

          <button
            className="lg:hidden flex flex-col gap-1.5 p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className="block w-6 h-0.5" style={{ backgroundColor: '#d4af37' }} />
            <span className="block w-6 h-0.5" style={{ backgroundColor: '#d4af37' }} />
            <span className="block w-6 h-0.5" style={{ backgroundColor: '#d4af37' }} />
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden" style={{ backgroundColor: '#0a0a0a', borderTop: '1px solid rgba(212,175,55,0.2)' }}>
            <ul className="flex flex-col py-4">
              {navLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="block px-6 py-3 text-sm tracking-widest hover:text-yellow-400 transition-colors"
                    style={{ color: '#fff' }}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link href="/booking" className="block px-6 py-3 text-sm tracking-widest font-semibold" style={{ color: '#d4af37' }} onClick={() => setMobileMenuOpen(false)}>
                  BOOK NOW
                </Link>
              </li>
            </ul>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <div className="relative py-16 px-4 text-center overflow-hidden" style={{ background: 'linear-gradient(180deg, #0a0a0a 0%, #000 100%)' }}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 50% 50%, #d4af37 0%, transparent 70%)' }} />
        <p className="text-xs tracking-[0.4em] mb-3" style={{ color: '#d4af37' }}>HASHTAG HAIR & BEAUTY STUDIO</p>
        <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          <span className="gold-text">Book Your Appointment</span>
        </h1>
        <p className="text-gray-400 text-base max-w-xl mx-auto">
          Reserve your spot at Kolkata&apos;s premium beauty destination. Select your service, pick a time, and we&apos;ll confirm your booking.
        </p>
        <div className="section-divider mt-6" />
      </div>

      {/* ── MAIN CONTENT ── */}
      <div className="max-w-4xl mx-auto px-4 pb-20">

        {submitted ? (
          /* ── SUCCESS STATE ── */
          <div className="text-center py-20">
            <div className="text-6xl mb-6">✅</div>
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              <span className="gold-text">Booking Request Sent!</span>
            </h2>
            <p className="text-gray-300 text-lg mb-2">Thank you, <span style={{ color: '#d4af37' }}>{form.name}</span>!</p>
            <p className="text-gray-400 mb-8 max-w-md mx-auto">
              Your booking request for <strong style={{ color: '#d4af37' }}>{form.service}</strong> on <strong style={{ color: '#d4af37' }}>{form.date}</strong> at <strong style={{ color: '#d4af37' }}>{form.time}</strong> has been received. We&apos;ll confirm via call or email shortly.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => { setSubmitted(false); setStep(1); setForm({ name: '', phone: '', email: '', service: '', date: '', time: '', notes: '' }); setSelectedCategory(''); }}
                className="px-8 py-3 rounded-xl font-semibold gold-btn"
              >
                Book Another
              </button>
              <Link href="/" className="px-8 py-3 rounded-xl font-semibold text-center" style={{ border: '1px solid rgba(212,175,55,0.4)', color: '#d4af37' }}>
                Back to Home
              </Link>
            </div>
          </div>
        ) : (
          <>
            {/* ── STEP INDICATOR ── */}
            <div className="flex items-center justify-center gap-0 my-10">
              {[
                { num: 1, label: 'Service' },
                { num: 2, label: 'Date & Time' },
                { num: 3, label: 'Your Details' },
              ].map((s, i) => (
                <React.Fragment key={s.num}>
                  <div className="flex flex-col items-center">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300"
                      style={{
                        background: step >= s.num ? 'linear-gradient(135deg, #d4af37 0%, #f5e17a 50%, #d4af37 100%)' : 'rgba(255,255,255,0.05)',
                        color: step >= s.num ? '#000' : '#666',
                        border: step >= s.num ? 'none' : '1px solid rgba(212,175,55,0.3)',
                      }}
                    >
                      {step > s.num ? '✓' : s.num}
                    </div>
                    <span className="text-xs mt-1 tracking-wider" style={{ color: step >= s.num ? '#d4af37' : '#555' }}>{s.label}</span>
                  </div>
                  {i < 2 && (
                    <div className="w-16 sm:w-24 h-px mx-2 mb-5" style={{ background: step > s.num ? '#d4af37' : 'rgba(212,175,55,0.2)' }} />
                  )}
                </React.Fragment>
              ))}
            </div>

            <form ref={formRef} onSubmit={handleSubmit}>

              {/* ══ STEP 1: SERVICE SELECTION ══ */}
              {step === 1 && (
                <div>
                  <h2 className="text-2xl font-bold mb-2 text-center" style={{ fontFamily: 'Playfair Display, serif', color: '#d4af37' }}>Choose Your Service</h2>
                  <p className="text-gray-400 text-sm text-center mb-8">Select the service you&apos;d like to book</p>

                  <div className="space-y-6">
                    {serviceOptions.map((cat) => (
                      <div key={cat.category}>
                        <button
                          type="button"
                          onClick={() => setSelectedCategory(selectedCategory === cat.category ? '' : cat.category)}
                          className="w-full flex items-center justify-between px-5 py-4 rounded-xl text-left transition-all duration-200"
                          style={{
                            background: selectedCategory === cat.category ? 'rgba(212,175,55,0.12)' : 'rgba(255,255,255,0.03)',
                            border: selectedCategory === cat.category ? '1px solid #d4af37' : '1px solid rgba(212,175,55,0.2)',
                          }}
                        >
                          <span className="font-semibold tracking-wide" style={{ color: '#d4af37' }}>{cat.category}</span>
                          <span style={{ color: '#d4af37' }}>{selectedCategory === cat.category ? '▲' : '▼'}</span>
                        </button>

                        {selectedCategory === cat.category && (
                          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 pl-2">
                            {cat.items.map((item) => (
                              <button
                                key={item}
                                type="button"
                                onClick={() => handleServiceSelect(item)}
                                className="text-left px-4 py-3 rounded-lg text-sm transition-all duration-200"
                                style={{
                                  background: form.service === item ? 'linear-gradient(135deg, #d4af37 0%, #f5e17a 50%, #d4af37 100%)' : 'rgba(255,255,255,0.04)',
                                  color: form.service === item ? '#000' : '#ccc',
                                  border: form.service === item ? 'none' : '1px solid rgba(212,175,55,0.15)',
                                  fontWeight: form.service === item ? '600' : '400',
                                }}
                              >
                                {form.service === item ? '✓ ' : ''}{item}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {form.service && (
                    <div className="mt-6 p-4 rounded-xl text-center" style={{ background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.3)' }}>
                      <p className="text-sm text-gray-400">Selected Service</p>
                      <p className="font-semibold mt-1" style={{ color: '#d4af37' }}>{form.service}</p>
                    </div>
                  )}

                  <div className="mt-8 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      disabled={!canProceedStep1}
                      className="px-8 py-3 rounded-xl font-semibold transition-all duration-200"
                      style={{
                        background: canProceedStep1 ? 'linear-gradient(135deg, #d4af37 0%, #f5e17a 50%, #d4af37 100%)' : 'rgba(255,255,255,0.05)',
                        color: canProceedStep1 ? '#000' : '#555',
                        cursor: canProceedStep1 ? 'pointer' : 'not-allowed',
                      }}
                    >
                      Next: Pick Date & Time →
                    </button>
                  </div>
                </div>
              )}

              {/* ══ STEP 2: DATE & TIME ══ */}
              {step === 2 && (
                <div>
                  <h2 className="text-2xl font-bold mb-2 text-center" style={{ fontFamily: 'Playfair Display, serif', color: '#d4af37' }}>Pick a Date & Time</h2>
                  <p className="text-gray-400 text-sm text-center mb-8">Choose when you&apos;d like to visit us</p>

                  {/* Date Picker */}
                  <div className="mb-8">
                    <label className="block text-sm font-medium mb-3 tracking-wider" style={{ color: '#d4af37' }}>SELECT DATE</label>
                    <input
                      type="date"
                      name="date"
                      value={form.date}
                      min={today}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl text-white text-base"
                      style={{
                        background: '#111',
                        border: form.date ? '1px solid #d4af37' : '1px solid rgba(212,175,55,0.3)',
                        colorScheme: 'dark',
                      }}
                    />
                  </div>

                  {/* Time Slots */}
                  <div className="mb-8">
                    <label className="block text-sm font-medium mb-3 tracking-wider" style={{ color: '#d4af37' }}>SELECT TIME SLOT</label>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                      {timeSlots.map((slot) => (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => handleTimeSelect(slot)}
                          className="py-2.5 px-2 rounded-lg text-xs font-medium transition-all duration-200 text-center"
                          style={{
                            background: form.time === slot ? 'linear-gradient(135deg, #d4af37 0%, #f5e17a 50%, #d4af37 100%)' : 'rgba(255,255,255,0.04)',
                            color: form.time === slot ? '#000' : '#aaa',
                            border: form.time === slot ? 'none' : '1px solid rgba(212,175,55,0.15)',
                            fontWeight: form.time === slot ? '700' : '400',
                          }}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Summary */}
                  {(form.date || form.time) && (
                    <div className="p-4 rounded-xl mb-6" style={{ background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.3)' }}>
                      <div className="flex flex-wrap gap-6 justify-center text-center">
                        {form.date && (
                          <div>
                            <p className="text-xs text-gray-500 tracking-wider">DATE</p>
                            <p className="font-semibold mt-1" style={{ color: '#d4af37' }}>{form.date}</p>
                          </div>
                        )}
                        {form.time && (
                          <div>
                            <p className="text-xs text-gray-500 tracking-wider">TIME</p>
                            <p className="font-semibold mt-1" style={{ color: '#d4af37' }}>{form.time}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between gap-4">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="px-6 py-3 rounded-xl font-semibold text-sm"
                      style={{ border: '1px solid rgba(212,175,55,0.3)', color: '#d4af37' }}
                    >
                      ← Back
                    </button>
                    <button
                      type="button"
                      onClick={() => setStep(3)}
                      disabled={!canProceedStep2}
                      className="px-8 py-3 rounded-xl font-semibold transition-all duration-200"
                      style={{
                        background: canProceedStep2 ? 'linear-gradient(135deg, #d4af37 0%, #f5e17a 50%, #d4af37 100%)' : 'rgba(255,255,255,0.05)',
                        color: canProceedStep2 ? '#000' : '#555',
                        cursor: canProceedStep2 ? 'pointer' : 'not-allowed',
                      }}
                    >
                      Next: Your Details →
                    </button>
                  </div>
                </div>
              )}

              {/* ══ STEP 3: CONTACT DETAILS ══ */}
              {step === 3 && (
                <div>
                  <h2 className="text-2xl font-bold mb-2 text-center" style={{ fontFamily: 'Playfair Display, serif', color: '#d4af37' }}>Your Contact Details</h2>
                  <p className="text-gray-400 text-sm text-center mb-8">We&apos;ll use this to confirm your appointment</p>

                  {/* Booking Summary */}
                  <div className="p-5 rounded-xl mb-8" style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.25)' }}>
                    <p className="text-xs tracking-widest text-gray-500 mb-3">BOOKING SUMMARY</p>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-xs text-gray-500">SERVICE</p>
                        <p className="text-sm font-semibold mt-1" style={{ color: '#d4af37' }}>{form.service}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">DATE</p>
                        <p className="text-sm font-semibold mt-1" style={{ color: '#d4af37' }}>{form.date}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">TIME</p>
                        <p className="text-sm font-semibold mt-1" style={{ color: '#d4af37' }}>{form.time}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium mb-2 tracking-wider" style={{ color: '#d4af37' }}>FULL NAME *</label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        required
                        className="contact-input w-full px-4 py-3 rounded-xl text-base"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2 tracking-wider" style={{ color: '#d4af37' }}>PHONE NUMBER *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+91 XXXXX XXXXX"
                        required
                        className="contact-input w-full px-4 py-3 rounded-xl text-base"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2 tracking-wider" style={{ color: '#d4af37' }}>EMAIL ADDRESS *</label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="you@example.com"
                        required
                        className="contact-input w-full px-4 py-3 rounded-xl text-base"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2 tracking-wider" style={{ color: '#d4af37' }}>SPECIAL REQUESTS <span className="text-gray-600 font-normal">(Optional)</span></label>
                      <textarea
                        name="notes"
                        value={form.notes}
                        onChange={handleChange}
                        placeholder="Any special requests or notes for our team..."
                        rows={3}
                        className="contact-input w-full px-4 py-3 rounded-xl text-base resize-none"
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="mt-4 p-4 rounded-xl text-sm text-center" style={{ background: 'rgba(255,80,80,0.1)', border: '1px solid rgba(255,80,80,0.3)', color: '#ff8080' }}>
                      {error}
                    </div>
                  )}

                  <div className="flex justify-between gap-4 mt-8">
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      className="px-6 py-3 rounded-xl font-semibold text-sm"
                      style={{ border: '1px solid rgba(212,175,55,0.3)', color: '#d4af37' }}
                    >
                      ← Back
                    </button>
                    <button
                      type="submit"
                      disabled={!canSubmit || submitting}
                      className="px-8 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center gap-2"
                      style={{
                        background: canSubmit && !submitting ? 'linear-gradient(135deg, #d4af37 0%, #f5e17a 50%, #d4af37 100%)' : 'rgba(255,255,255,0.05)',
                        color: canSubmit && !submitting ? '#000' : '#555',
                        cursor: canSubmit && !submitting ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {submitting ? (
                        <>
                          <span className="inline-block w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                          Sending...
                        </>
                      ) : (
                        '✉ Confirm Booking'
                      )}
                    </button>
                  </div>

                  <p className="text-center text-xs text-gray-600 mt-4">
                    By confirming, you agree to be contacted by our team for appointment confirmation.
                  </p>
                </div>
              )}

            </form>
          </>
        )}
      </div>

      {/* ── FOOTER ── */}
      <footer className="py-8 text-center text-sm text-gray-600" style={{ borderTop: '1px solid rgba(212,175,55,0.15)' }}>
        <p>© 2026 Hashtag Hair & Beauty Studio. All rights reserved.</p>
        <p className="mt-1">
          <Link href="/" className="hover:text-yellow-400 transition-colors">Back to Home</Link>
          {' · '}
          <a href="tel:+919876543210" className="hover:text-yellow-400 transition-colors">Call Us</a>
        </p>
      </footer>
    </div>
  );
}
